// FacePicker — hover/click по граням тел.
//
// Идея: пробрасываем pointer-events с body-mesh, по hit'у через intersect.face
// определяем большую плоскую грань (через facePicking.buildFaceGroups), рисуем
// поверх неё полупрозрачный квадрат, при клике — отдаём данные грани в callback.
//
// Используется в двух сценариях:
//   - mode='select_plane' (создание эскиза) — onPick получает planeRef
//     ({ plane, offset, center }), Viewport создаёт sketch на этой грани.
//   - mode='view3d' (выделение грани для extrude/etc.) — onPick получает
//     полные данные грани (planeRef + контур в 3D), callback пишет в selection.
//
// Ограничение MVP: пока поддерживаем только грани с нормалью параллельной
// одной из главных осей (XY/XZ/YZ + offset). 99% случаев на простых моделях.
// Custom-нормали (наклонные грани) — позже через replicad.makePlane.

import React, { useMemo, useState } from 'react';
import * as THREE from 'three';
// Нужны для снапа к вершинам по экранному расстоянию (см. measureMode ниже).
import { useThree } from '@react-three/fiber';
import useCadStore from '../store/useCadStore';
import { buildFaceGroups, computeFaceOverlay, extractFaceLoop } from './facePicking';
import { toast } from '../ui/Toasts';
import { addMeasurePick } from './measureController';


// Возвращаем данные плоскости грани для replicad sketchOnPlane.
//
// Если нормаль параллельна одной из главных осей (XY/XZ/YZ) — возвращаем
// именованную плоскость + offset (быстрый и проверенный путь).
//
// Иначе — возвращаем custom-плоскость через {origin, xDir, normal}, которую
// worker превратит в `new Plane(origin, xDir, normal)`. Поддерживает любые
// углы и наклонные грани.
function faceToPlaneRef(face) {
    // ВСЕГДА CUSTOM-плоскость с базисом реальной грани (origin/xDir/normal).
    // Раньше для осевых граней возвращалась именованная плоскость (XY/XZ/YZ)
    // с фиксированным базисом — но у граней с ПРОТИВОПОЛОЖНОЙ нормалью
    // (задняя/нижняя стенка) фикс. базис не совпадал с фактической нормалью,
    // из-за чего камера, плоскость рисования и клик рассинхронивались:
    // эскиз ложился «за телом» и был не виден. CUSTOM с настоящим базисом
    // грани согласует всё (камера смотрит по normal, 2D-геометрия — в u/v).
    const n = face.normal;
    // origin = точка на плоскости грани ближе всего к (0,0,0) = n * d.
    const origin = [n.x * face.d, n.y * face.d, n.z * face.d];
    // xDir — перпендикуляр к нормали (тот же выбор, что в computeFaceOverlay,
    // чтобы 2D-базис эскиза совпал с базисом overlay-подсветки грани).
    const ref = (Math.abs(n.x) < 0.9) ? [1, 0, 0] : [0, 1, 0];
    const xx = ref[1] * n.z - ref[2] * n.y;
    const xy = ref[2] * n.x - ref[0] * n.z;
    const xz = ref[0] * n.y - ref[1] * n.x;
    const len = Math.sqrt(xx * xx + xy * xy + xz * xz) || 1;
    const xDir = [xx / len, xy / len, xz / len];
    return {
        plane: 'CUSTOM',
        origin,
        xDir,
        normal: [n.x, n.y, n.z],
        worldNormal: [n.x, n.y, n.z],  // сторона для камеры
        offset: 0,
    };
}


// Для одного тела: обёртка с прозрачным mesh для pointer-events + overlay-квадрат.
const BodyFaceLayer = ({ bodyId, mesh, onPick, hoveredKey, setHoveredKey, withLoop, measureMode }) => {
    const { positions, indices, normals } = mesh;
    // Камера и канвас — для снапа к вершинам по ЭКРАННОМУ расстоянию (см. onClick).
    const { camera, gl } = useThree();
    // Глобальное выделение из store — для подсветки selected-грани.
    const selection = useCadStore((s) => s.selection);
    // Все выбранные грани ЭТОГО тела (для мультивыбора Ctrl/Shift).
    const selectedFaceIds = useMemo(() => {
        if (!withLoop || selection.kind !== 'face') return [];
        const out = [];
        for (const ref of selection.ids) {
            const [bid, fpart] = ref.split('/');
            if (bid !== bodyId) continue;
            const m = /^face_(\d+)$/.exec(fpart);
            if (m) out.push(parseInt(m[1], 10));
        }
        return out;
    }, [withLoop, selection.kind, selection.ids, bodyId]);

    // Группируем триангуляцию по плоским граням.
    const { triToFace, faces } = useMemo(
        () => buildFaceGroups(positions, indices, normals),
        [positions, indices, normals]
    );

    // Геометрия для hit-test (та же что у BodyMesh).
    const geometry = useMemo(() => {
        const g = new THREE.BufferGeometry();
        g.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        g.setIndex(new THREE.BufferAttribute(indices, 1));
        if (normals && normals.length) {
            g.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
        } else {
            g.computeVertexNormals();
        }
        return g;
    }, [positions, indices, normals]);

    // Текущая грань под курсором (faceIndex локальный для этого тела).
    const [hoverFace, setHoverFace] = useState(null);
    const myKey = bodyId;

    // Геометрия hover-грани: только её треугольники (форма точно повторяет
    // реальную поверхность, включая закруглённые после fillet/chamfer).
    const hoverFaceGeom = useMemo(() => {
        if (hoverFace == null) return null;
        const face = faces[hoverFace];
        if (!face) return null;
        return buildFaceGeometry(positions, indices, normals, triToFace, hoverFace, face.normal);
    }, [hoverFace, faces, positions, indices, normals, triToFace]);

    // Геометрии всех выделенных граней этого тела (мультивыбор).
    const selectedFaceGeoms = useMemo(() => {
        const out = [];
        for (const fid of selectedFaceIds) {
            const face = faces[fid];
            if (!face) continue;
            const g = buildFaceGeometry(positions, indices, normals, triToFace, fid, face.normal);
            if (g) out.push({ fid, geometry: g });
        }
        return out;
    }, [selectedFaceIds, faces, positions, indices, normals, triToFace]);

    // Только когда мы — текущая «hovered» — рисуем hover-overlay.
    const showOverlay = hoveredKey === myKey && hoverFaceGeom != null;

    const onMove = (e) => {
        /*
         * Приоритет ребра при наведении — только в measure-режиме, по той же
         * причине, что и в onClick ниже: raycaster ловит рёбра в радиусе 1.5 мм
         * по ВСЕЙ глубине сцены, поэтому у бруска подсветка грани гасла почти
         * везде и было не понять, что выберется по клику.
         */
        const ints = e.intersections || [];
        const hitsEdge = ints.some((it) => it.object?.userData?.kind === 'edge');
        if (hitsEdge && measureMode) {
            if (hoverFace != null) setHoverFace(null);
            if (hoveredKey === myKey) setHoveredKey(null);
            return;
        }
        // r3f даёт e.face = {a,b,c,materialIndex} и e.faceIndex = i (индекс треугольника)
        const triIdx = e.faceIndex;
        if (triIdx == null) return;
        const fIdx = triToFace[triIdx];
        if (fIdx === hoverFace && hoveredKey === myKey) return;
        setHoverFace(fIdx);
        setHoveredKey(myKey);
    };

    const onOut = () => {
        setHoverFace(null);
        if (hoveredKey === myKey) setHoveredKey(null);
    };

    const onClick = (e) => {
        // Если в intersections рядом есть ребро — отдаём приоритет ему,
        // чтобы по тонкому ребру можно было выбрать edge без перехвата face.
        // r3f сортирует intersections по distance; если первое (ближайшее)
        // intersection — edge, то наш FacePicker.onClick всё равно вызвался
        // (т.к. оба объекта рядом по distance). Проверяем и пропускаем.
        const ints = e.intersections || [];
        const hitsEdge = ints.some((it) => {
            // userData может быть и на самом object, и в parent group
            const ud = it.object?.userData;
            return ud && ud.kind === 'edge';
        });
        /*
         * Приоритет ребра — ТОЛЬКО в режиме измерения (как во Fusion: наведение
         * на ребро выбирает ребро).
         *
         * 🔴 Раньше условие было `!withLoop || measureMode`, и это ломало выбор
         * плоскости для эскиза. В режиме select_plane FacePicker рендерится без
         * withLoop (Viewport ~1244), поэтому `!withLoop` давало true, и клик
         * молча отбрасывался, если в луче оказалось ЛЮБОЕ ребро.
         *
         * А оказывалось оно почти всегда: raycaster ловит линию в радиусе
         * threshold=1.5 мм (Viewport ~1156), и проверка смотрит ВЕСЬ список
         * intersections — то есть рёбра на дальней стороне тела тоже блокировали
         * клик по ближней грани. У обычного бруска кликнуть по верхней грани
         * было практически невозможно: выбиралось ребро (в свойствах «Тип: edge»),
         * а эскиз не создавался.
         *
         * При выборе плоскости и при выделении грани рёбра не нужны вовсе —
         * там выбирают только грани, поэтому перехват тут вреден.
         */
        if (hitsEdge && measureMode) {
            return;
        }
        e.stopPropagation();
        const triIdx = e.faceIndex;
        if (triIdx == null) return;
        const fIdx = triToFace[triIdx];
        const face = faces[fIdx];
        if (!face) return;
        const ref = faceToPlaneRef(face);
        if (!ref) return;

        // === Режим измерения: пик грани (или вершины при снапе к углу) ===
        if (measureMode) {
            const clickP = [e.point.x, e.point.y, e.point.z];
            const ov = computeFaceOverlay(positions, face);
            const center = ov ? [ov.center.x, ov.center.y, ov.center.z] : clickP;
            /*
             * Снап к вершине — по ЭКРАННОМУ расстоянию, а не по миллиметрам.
             *
             * Раньше порог был жёстким: 3.5 мм в координатах модели. На детали
             * 30 мм это пара пикселей — попасть в угол практически невозможно
             * («точки не выделяются»), а на модели в метр те же 3.5 мм ловили
             * бы вершину случайно. Теперь считаем в пикселях экрана, как во
             * Fusion: радиус фиксирован визуально при любом масштабе и зуме.
             */
            const loop = extractFaceLoop(positions, indices, face, triToFace);
            let snapVert = null, bestD = Infinity;
            if (loop && loop.points3d) {
                const rect = gl.domElement.getBoundingClientRect();
                const toScreen = (x, y, z) => {
                    const v = new THREE.Vector3(x, y, z).project(camera);
                    return {
                        x: (v.x + 1) / 2 * rect.width,
                        y: (-v.y + 1) / 2 * rect.height,
                    };
                };
                const cursor = {
                    x: e.nativeEvent.clientX - rect.left,
                    y: e.nativeEvent.clientY - rect.top,
                };
                for (const p of loop.points3d) {
                    const s = toScreen(p.x, p.y, p.z);
                    const d = Math.hypot(s.x - cursor.x, s.y - cursor.y);
                    if (d < bestD) { bestD = d; snapVert = [p.x, p.y, p.z]; }
                }
            }
            // 14 px — комфортный радиус захвата: угол ловится уверенно, но
            // клик по середине грани по-прежнему выбирает грань.
            if (snapVert && bestD <= 14) {
                addMeasurePick({
                    bodyId, kind: 'vertex', point: snapVert,
                    render: { point: snapVert },
                });
            } else {
                addMeasurePick({
                    bodyId, kind: 'face',
                    point: clickP,
                    normal: [face.normal.x, face.normal.y, face.normal.z],
                    render: { point: center },
                });
            }
            return;
        }
        /*
         * КРИВУЮ ГРАНЬ НЕ ОТДАЁМ. Выдавливание и эскиз строятся на плоскости;
         * у купола или цилиндра её нет. Раньше такой клик уходил в ядро и ронял
         * его вырожденным профилем — теперь честно говорим, что грань не годится.
         */
        if (face.curved) {
            notifyCurvedFace();
            return;
        }
        // Центр грани в world — нужен чтобы камера зацентрировалась
        // именно на грани (а не на проекции origin'а на плоскость).
        const ov = computeFaceOverlay(positions, face);
        if (ov) {
            ref.center = [ov.center.x, ov.center.y, ov.center.z];
        }
        // Контур грани — нужен и для extrude, и для snap'а в эскизе.
        const loop = extractFaceLoop(positions, indices, face, triToFace);
        const points3d = loop ? loop.points3d.map((p) => [p.x, p.y, p.z]) : null;
        if (points3d) ref.points3d = points3d;
        /* Внутренние контуры кольцевой грани: без них «Выдавить грань»
           строит сплошной круг вместо кольца и вырезание падает. */
        if (loop && loop.holes3d) {
            ref.holes3d = loop.holes3d.map((h) => h.map((p) => [p.x, p.y, p.z]));
        }

        // Ctrl/Shift — множественный выбор граней (передаём флаг в callback).
        const ne = e.nativeEvent;
        const multi = e.shiftKey || e.ctrlKey || e.metaKey
            || ne?.shiftKey || ne?.ctrlKey || ne?.metaKey;
        // withLoop=true — режим выделения для extrude.
        if (withLoop) {
            onPick({
                bodyId,
                faceId: fIdx,
                planeRef: ref,
                normal: face.normal,
                points3d,
                multi,
            });
            return;
        }
        // Эскиз на грани: запоминаем тело-владелец грани — нужно, чтобы
        // extrude add/cut целился именно в него (а не в «последнее тело»).
        ref.bodyId = bodyId;
        onPick(ref);
    };

    return (
        <group userData={{ bodyId, isPickerLayer: true }}>
            <mesh
                geometry={geometry}
                onPointerMove={onMove}
                onPointerOut={onOut}
                onClick={onClick}
            >
                {/* Полностью прозрачный материал — мы не закрашиваем тело,
                    но получаем интеракции. Cuts the mesh видимо в режиме select_plane. */}
                <meshBasicMaterial
                    transparent
                    opacity={0}
                    depthWrite={false}
                    side={THREE.DoubleSide}
                />
            </mesh>

            {showOverlay && (
                <FaceOverlay geometry={hoverFaceGeom}/>
            )}
            {selectedFaceGeoms.map(({ fid, geometry }) => (
                <FaceOverlay key={fid} geometry={geometry} selected/>
            ))}
        </group>
    );
};


// Подсветка грани РЕАЛЬНОЙ формы — рендерим только треугольники этой грани.
// Геометрия уже сдвинута по нормали на 0.05мм чтобы не было z-fighting с телом.
// selected=true — синяя подсветка (vs оранжевый hover).
const FaceOverlay = ({ geometry, selected }) => {
    const fillColor = selected ? '#3a78ff' : '#ffb14a';
    return (
        <mesh geometry={geometry} renderOrder={2}>
            <meshBasicMaterial
                color={fillColor}
                transparent
                opacity={0.55}
                side={THREE.DoubleSide}
                depthWrite={false}
                polygonOffset
                polygonOffsetFactor={-2}
                polygonOffsetUnits={-2}
            />
        </mesh>
    );
};


// Строим BufferGeometry только из треугольников указанной грани.
// Каждый треугольник смещаем чуть по нормали грани чтобы overlay был
// поверх оригинального тела. polygonOffset в материале — дополнительная
// страховка против z-fighting.
/*
 * Сообщение о непригодной грани — тем же каналом тостов, что и остальные
 * ошибки редактора (ui/Toasts).
 */
function notifyCurvedFace() {
    toast('Эта поверхность изогнутая — выдавить её нельзя. '
        + 'Выбери плоскую грань или построй эскиз на рабочей плоскости.', 'error');
}


function buildFaceGeometry(positions, indices, normals, triToFace, faceId, faceNormal) {
    if (faceId == null || !triToFace) return null;
    // Собираем индексы треугольников этой грани
    const triList = [];
    for (let t = 0; t < triToFace.length; t++) {
        if (triToFace[t] === faceId) triList.push(t);
    }
    if (triList.length === 0) return null;

    // Формируем массив позиций (без переиспользования вершин — простой формат).
    // 3 вершины на каждый треугольник.
    const vertCount = triList.length * 3;
    const posArr = new Float32Array(vertCount * 3);
    const normArr = new Float32Array(vertCount * 3);
    // Сдвигаем по нормали грани чтобы избежать z-fighting (отдельно от
    // polygonOffset — последний может работать неточно на некоторых GPU).
    const lift = 0.04;
    const nx = faceNormal.x * lift;
    const ny = faceNormal.y * lift;
    const nz = faceNormal.z * lift;

    for (let i = 0; i < triList.length; i++) {
        const t = triList[i];
        for (let k = 0; k < 3; k++) {
            const vi = indices[t * 3 + k];
            const dst = (i * 3 + k) * 3;
            posArr[dst]     = positions[vi * 3]     + nx;
            posArr[dst + 1] = positions[vi * 3 + 1] + ny;
            posArr[dst + 2] = positions[vi * 3 + 2] + nz;
            // Нормали: используем нормаль грани (плоская грань) либо vertex-normal
            if (normals && normals.length > vi * 3 + 2) {
                normArr[dst]     = normals[vi * 3];
                normArr[dst + 1] = normals[vi * 3 + 1];
                normArr[dst + 2] = normals[vi * 3 + 2];
            } else {
                normArr[dst]     = faceNormal.x;
                normArr[dst + 1] = faceNormal.y;
                normArr[dst + 2] = faceNormal.z;
            }
        }
    }

    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(posArr, 3));
    g.setAttribute('normal', new THREE.BufferAttribute(normArr, 3));
    return g;
}


/** Внешний компонент — рендерится в Viewport.
 *  Перебирает все видимые тела и навешивает FaceLayer на каждое.
 *  withLoop=true — извлекать контур грани (для extrude/etc.). */
const FacePicker = ({ onPick, withLoop, measureMode }) => {
    const bodyMeshes = useCadStore((s) => s.bodyMeshes);
    const visibility = useCadStore((s) => s.bodyVisibility);
    const [hoveredKey, setHoveredKey] = useState(null);

    const items = useMemo(() => {
        const list = [];
        for (const [id, mesh] of bodyMeshes.entries()) {
            const vis = visibility.has(id) ? visibility.get(id) : true;
            if (!vis) continue;
            list.push({ id, mesh });
        }
        return list;
    }, [bodyMeshes, visibility]);

    return (
        <group>
            {items.map(({ id, mesh }) => (
                <BodyFaceLayer
                    key={id}
                    bodyId={id}
                    mesh={mesh}
                    onPick={onPick}
                    hoveredKey={hoveredKey}
                    setHoveredKey={setHoveredKey}
                    withLoop={withLoop}
                    measureMode={measureMode}
                />
            ))}
        </group>
    );
};

export default FacePicker;
